import React from "react";
import "./FlyerProductSection.css";
import {App as AntdApp, Button, Collapse, DatePicker, Divider, Input, Modal, Select, Space, Switch, Tag, Tooltip, Upload, Image, Spin, Alert, Typography} from "antd";
import {PlusOutlined, FileImageOutlined, EditOutlined, DeleteOutlined, FolderOpenOutlined, StarOutlined, CopyOutlined, SnippetsOutlined} from "@ant-design/icons";

const {Text} = Typography;

import FloatingField from "@shared/components/FloatingField.jsx";
import RefEditorModal from "./RefEditorModal.jsx";
import api from "@/api/axios.js";
// Formati accettati per foto prodotto (inclusi PSD, AI, PDF, TIFF convertiti lato server)
const PRODUCT_PHOTO_ACCEPT = ".webp,.gif,.png,.jpg,.jpeg,.psd,.ai,.pdf,.tif,.tiff";
const PRODUCT_PHOTO_EXTS = new Set([".webp", ".gif", ".png", ".jpg", ".jpeg", ".psd", ".ai", ".pdf", ".tif", ".tiff"]);

// Import da moduli centralizzati
import {PRICE_FIELD_KEYS, SERIAL_READONLY_HINT} from "../constants/fields.js";
import {FIXED_PRODUCT_FIELD_KEYS, buildEditorColumns} from "@modules/fields/utils/editorLayout.js";
import {normalizePrice, normalizeProductPhotoItems} from "../utils/normalizers.js";
import {parseDateValue, toFieldKey} from "../utils/formatters.js";
import {
	MemoizedTextField,
	MemoizedTextArea,
	MemoizedNumberField,
	MemoizedDateField,
	MemoizedSwitchField,
	MemoizedSelectField
} from "./fields/index.js";
import {useFieldColorMap, getFieldColor, toHexColor} from "@modules/fields/utils/fieldColors.js";
import {useTagFieldClipboard, useGenericFieldClipboard} from "../utils/tagFieldClipboard.js";
import {normalizeCustomerCodeInput} from "../utils/photoCodes.js";

function normalizeGroupKey(groupValue)
{
	if (typeof groupValue !== "string") return "";
	return groupValue.replace(/\\/g, "/").trim();
}

function normalizeGroupKeys(groupValues)
{
	const values = Array.isArray(groupValues) ? groupValues : [groupValues];
	return [...new Set(values.map(normalizeGroupKey).filter(Boolean))];
}

function resolveRefGroupKeys(ref)
{
	const explicitGroups = normalizeGroupKeys([...(ref?.sourceGroups || []), ref?.sourceGroup]);
	if (explicitGroups.length)
		return explicitGroups;

	const sourceRelativePaths = normalizeGroupKeys([...(ref?.sourceRelativePaths || []), ref?.sourceRelativePath]);
	const pathGroups = sourceRelativePaths
		.filter((sourceRelativePath) => sourceRelativePath.includes("/"))
		.map((sourceRelativePath) => sourceRelativePath.split("/").slice(0, -1).join("/"))
		.filter(Boolean);
	if (pathGroups.length)
		return [...new Set(pathGroups)];

	const originalNamePath = normalizeGroupKey(ref?.originalName);
	const parts = originalNamePath.split("/").filter(Boolean);
	if (parts.length >= 3)
	{
		return [parts.slice(1, -1).join("/")];
	}

	return [];
}

function sortGroupKeys(keys)
{
	return [...keys].sort((a, b) => a.localeCompare(b, "it", {sensitivity: "base", numeric: true}));
}

function extractPageNumber(groupKey)
{
	const segments = normalizeGroupKey(groupKey).split("/").filter(Boolean);
	const parseSegment = (segment) =>
	{
		const normalized = segment.replace(/[-_]/g, " ").trim();
		const pageMatch = normalized.match(/(?:^|\b)(?:pag(?:ina)?|page|p)\.?\s*(\d+)(?:\b|$)/i);
		if (pageMatch)
			return Number(pageMatch[1]);

		if (/^\d+$/.test(normalized))
			return Number(normalized);

		return null;
	};

	for (const segment of segments)
	{
		const pageNumber = parseSegment(segment);
		if (Number.isFinite(pageNumber))
			return pageNumber;
	}

	return null;
}

function groupRefs(refsList)
{
	const groupedMap = new Map();
	const groupOrder = [];
	const ungrouped = [];

	refsList.forEach((ref) =>
	{
		const sourceGroups = resolveRefGroupKeys(ref);
		if (!sourceGroups.length)
		{
			ungrouped.push(ref);
			return;
		}

		sourceGroups.forEach((sourceGroup) =>
		{
			if (!groupedMap.has(sourceGroup))
			{
				groupedMap.set(sourceGroup, []);
				groupOrder.push(sourceGroup);
			}

			groupedMap.get(sourceGroup).push(ref);
		});
	});

	return {
		groupedMap,
		groupOrder: sortGroupKeys(groupOrder),
		ungrouped,
		hasGroups: groupOrder.length > 0
	};
}

export default function FlyerProductSection({
	value,
	onChange,
	categoryOptions,
	categoryLoading = false,
	onCreateCategory,
	classOptions = [],
	classLoading = false,
	onCreateClass,
	fundOptions = [],
	fundLoading = false,
	onCreateFund,
	badgeOptions = [],
	badgeLoading = false,
	onCreateBadge,
	filterOptions = [],
	filterLoading = false,
	onCreateFilter,
	mechanicOptions = [],
	mechanicLoading = false,
	onCreateMechanic,
	productFields = [],
	productFieldsLoading = false,
	genericTaxonomies = {},
	disabled = false,
	onServerCardUpdate,
	availablePages = []
})
{
	const data = value?.ai || {};
	const {message} = AntdApp.useApp();
	const cardId = value?._id;
	const flyerId = value?.flyerId;
	const photoInputRef = React.useRef(null);

	const fieldColorMap = useFieldColorMap();
	// onCopySingleValue è passato esplicitamente da ogni chiamante (bollino/filtro/meccanica hanno
	// clipboard indipendenti, vedi sotto) invece di essere risolto qui dentro, per non dipendere
	// dall'ordine di dichiarazione degli hook di clipboard rispetto a questa callback.
	const makeTagRender = React.useCallback((fieldKey, onCopySingleValue) => (tagProps) =>
	{
		const {label, value: tagValue, closable, onClose} = tagProps;
		// Il valore viene prima, le azioni dopo: copia sta accanto alla ✕ che AntD appende in
		// coda, così i due comandi del tag stanno insieme sul lato destro invece che agli
		// estremi opposti con l'etichetta schiacciata in mezzo. Copia compare all'hover del
		// singolo tag: con otto bollini a video, otto icone sempre accese sono solo rumore.
		return (
			<Tag
				className="fm-value-tag"
				color={getFieldColor(fieldColorMap, fieldKey)}
				closable={closable}
				onClose={onClose}
				onMouseDown={(e) => e.preventDefault()}
			>
				<span className="fm-value-tag-label">{label}</span>
				{onCopySingleValue && (
					<Tooltip title="Copia solo questo valore" mouseEnterDelay={.4}>
						<span
							role="button"
							tabIndex={0}
							aria-label="Copia solo questo valore"
							className="fm-value-tag-copy"
							onMouseDown={(e) => e.preventDefault()}
							onClick={(e) =>
							{
								e.stopPropagation();
								onCopySingleValue([tagValue]);
							}}
							onKeyDown={(e) =>
							{
								if (e.key !== "Enter" && e.key !== " ")
									return;
								e.preventDefault();
								e.stopPropagation();
								onCopySingleValue([tagValue]);
							}}
						>
							<CopyOutlined/>
						</span>
					</Tooltip>
				)}
			</Tag>
		);
	}, [fieldColorMap]);
	// Colora le icone Copia/Incolla con lo stesso colore assegnato al campo in Impostazioni:
	// rinforza visivamente che copia/incolla resta sempre dentro lo stesso campo (colori diversi
	// tra campi diversi), coerente con i Tag che già usano questo colore per lo stesso campo.
	const fieldIconColor = React.useCallback(
		(fieldKey) => toHexColor(getFieldColor(fieldColorMap, fieldKey)),
		[fieldColorMap]
	);

	const [categorySearch, setCategorySearch] = React.useState("");
	const [classSearch, setClassSearch] = React.useState("");
	const [fundSearch, setFundSearch] = React.useState("");
	const [badgeSearch, setBadgeSearch] = React.useState("");
	const [filterSearch, setFilterSearch] = React.useState("");
	const [mechanicSearch, setMechanicSearch] = React.useState("");
	// Testo cercato nelle tassonomie generiche (fondo, banner, focus...), per chiave campo:
	// serve a mostrare "Aggiungi" come nelle tassonomie storiche.
	const [genericSearch, setGenericSearch] = React.useState({});
	// Copia/incolla di Meccanica/Bollino/Filtro/Categoria/Classe/Fondi tra card diverse (renderField
	// è una callback invocata in loop su ogni campo: gli hook vanno chiamati qui, a livello top del
	// componente, non lì dentro). Ogni campo ha il proprio clipboard indipendente (chiave diversa
	// nello store), quindi copiare da un campo e incollare in un altro non è possibile per
	// costruzione.
	const mechanicClipboard = useTagFieldClipboard("meccanica");
	const badgeClipboard = useTagFieldClipboard("bollino");
	const filterClipboard = useTagFieldClipboard("filtro");
	const categoryClipboard = useTagFieldClipboard("categoria");
	const classClipboard = useTagFieldClipboard("classe");
	const fundClipboard = useTagFieldClipboard("fondi");
	// Clipboard generico per i campi di testo libero (Note, Descrizione estesa, o un campo TEXTAREA
	// creato su misura per una cooperativa): l'elenco di questi campi non è fisso, quindi un solo
	// hook gestisce "l'ultimo campo copiato" invece di uno slot per campo (vedi commento in
	// tagFieldClipboard.js).
	const genericTextClipboard = useGenericFieldClipboard();
	const [photoProcessing, setPhotoProcessing] = React.useState(false);
	const [refsOpen, setRefsOpen] = React.useState(false);
	const [refs, setRefs] = React.useState([]);
	const [refsLoading, setRefsLoading] = React.useState(false);
	const photoUploadQueueRef = React.useRef(Promise.resolve());
	const [photoDragActive, setPhotoDragActive] = React.useState(false);
	const [photoUploadProgress, setPhotoUploadProgress] = React.useState({active: false, percent: 0, current: 0, total: 0});

	// Editor Fotoprodotto
	const [editingPhotoRelPath, setEditingPhotoRelPath] = React.useState("");
	const [photoCacheKey, setPhotoCacheKey] = React.useState(() => Date.now());

	const handleSaveComposition = React.useCallback(async (newComposition, canvasConfig, targetRelPath) =>
	{
		if (!cardId) return;
		try
		{
			setPhotoProcessing(true);
			const {data: resp} = await api.patch(`/api/products/${cardId}/photo/composition`, {
				composition: newComposition,
				canvasConfig,
				relPath: targetRelPath
			});

			if (resp.ok && resp.card)
			{
				onServerCardUpdate?.(resp.card);
				setPhotoCacheKey(Date.now());
				message.success("Foto prodotto aggiornata");
			}
		}
		catch (e)
		{
			console.error(e);
			message.error("Errore aggiornamento foto");
		}
		finally
		{
			setPhotoProcessing(false);
		}
	}, [cardId, onServerCardUpdate, message]);

	const resolvePhotoUrl = React.useCallback((raw) =>
	{
		const value = String(raw || "").trim();
		if (!value)
			return "";

		if (/^https?:\/\//i.test(value))
			return value;

		const apiBase = import.meta.env.VITE_API_URL || "";

		if (value.startsWith("/"))
			return `${apiBase}${value}`;

		return `${apiBase}/media/flyers/${value}`;
	}, []);

	const normalizePhotoUrl = React.useCallback((raw) =>
	{
		const value = String(raw || "").trim();
		if (!value)
			return "";
		if (value.startsWith("data:"))
			return value;
		return resolvePhotoUrl(value);
	}, [resolvePhotoUrl]);

	const handleAddCategory = React.useCallback(async () =>
	{
		const trimmed = categorySearch.trim();
		if (!trimmed) return;
		try
		{
			const created = await onCreateCategory?.(trimmed);
			if (created?.value) onChange?.("categoria", created.value);
		}
		finally
		{
			setCategorySearch("");
		}
	}, [categorySearch, onChange, onCreateCategory]);

	const handleAddClass = React.useCallback(async () =>
	{
		const trimmed = classSearch.trim();
		if (!trimmed) return;
		try
		{
			const created = await onCreateClass?.(trimmed);
			if (created?.value) onChange?.("classe", created.value);
		}
		finally
		{
			setClassSearch("");
		}
	}, [classSearch, onChange, onCreateClass]);

	const handleAddFund = React.useCallback(async () =>
	{
		const trimmed = fundSearch.trim();
		if (!trimmed) return;
		try
		{
			const created = await onCreateFund?.(trimmed);
			if (created?.value) onChange?.("fondi", created.value);
		}
		finally
		{
			setFundSearch("");
		}
	}, [fundSearch, onChange, onCreateFund]);

	const handleAddBadge = React.useCallback(async () =>
	{
		const trimmed = badgeSearch.trim();
		if (!trimmed) return;
		try
		{
			const created = await onCreateBadge?.(trimmed);
			if (created?.value)
			{
				// Bollino è un array, quindi aggiungiamo il valore
				const current = Array.isArray(data?.bollino) ? data.bollino : [];
				onChange?.("bollino", [...current, created.value]);
			}
		}
		finally
		{
			setBadgeSearch("");
		}
	}, [badgeSearch, onChange, onCreateBadge, data?.bollino]);

	const handleAddFilter = React.useCallback(async () =>
	{
		const trimmed = filterSearch.trim();
		if (!trimmed) return;
		try
		{
			const created = await onCreateFilter?.(trimmed);
			if (created?.value)
			{
				const current = Array.isArray(data?.filtro)
					? data.filtro
					: (data?.filtro ? [data.filtro] : []);
				onChange?.("filtro", [...current, created.value]);
			}
		}
		finally
		{
			setFilterSearch("");
		}
	}, [filterSearch, onChange, onCreateFilter, data?.filtro]);

	const handleAddMechanic = React.useCallback(async () =>
	{
		const trimmed = mechanicSearch.trim();
		if (!trimmed) return;
		try
		{
			const created = await onCreateMechanic?.(trimmed);
			if (created?.value)
			{
				// Meccanica è un array, quindi aggiungiamo il valore
				const current = Array.isArray(data?.meccanica) ? data.meccanica : [];
				onChange?.("meccanica", [...current, created.value]);
			}
		}
		finally
		{
			setMechanicSearch("");
		}
	}, [mechanicSearch, onChange, onCreateMechanic, data?.meccanica]);

	const selloutValue = React.useMemo(() =>
			data?.sellout ? parseDateValue(data.sellout) : null,
		[data?.sellout]);

	const normalizedFields = React.useMemo(() =>
	{
		const seen = new Set();
		const list = [];
		for (const field of productFields)
		{
			const key = field?.key ? String(field.key) : toFieldKey(field?.name);
			if (!key || seen.has(key) || FIXED_PRODUCT_FIELD_KEYS.has(key))
				continue;

			const rawType = field?.type || field?.inputType || "";
			const type = String(rawType).toUpperCase() || "TEXT";
			const label = field?.name || field?.label || key;
			const dateMode = field?.dateMode;

			seen.add(key);
			list.push({key, label, type, dateMode, editorColumn: field?.editorColumn, editorPosition: field?.editorPosition});
		}
		return list;
	}, [productFields]);

	const columns = React.useMemo(() => buildEditorColumns(normalizedFields), [normalizedFields]);

	const productPhotoItems = React.useMemo(() => normalizeProductPhotoItems(data), [data]);

	const editingPhoto = React.useMemo(() =>
	{
		if (!editingPhotoRelPath)
			return null;
		return productPhotoItems.find((item) => item.relPath === editingPhotoRelPath) || null;
	}, [productPhotoItems, editingPhotoRelPath]);

	React.useEffect(() =>
	{
		if (!editingPhotoRelPath)
			return;
		const exists = productPhotoItems.some((item) => item.relPath === editingPhotoRelPath);
		if (!exists)
			setEditingPhotoRelPath("");
	}, [editingPhotoRelPath, productPhotoItems]);

	const buildPhotoRenderUrl = React.useCallback((raw) =>
	{
		const normalized = normalizePhotoUrl(raw);
		if (!normalized)
			return "";
		if (normalized.startsWith("data:"))
			return normalized;
		const separator = normalized.includes("?") ? "&" : "?";
		return `${normalized}${separator}t=${photoCacheKey}`;
	}, [normalizePhotoUrl, photoCacheKey]);

	const renderPhotoThumbnail = React.useCallback((photo) =>
	{
		const cc = photo.canvasConfig;
		const imgSrc = buildPhotoRenderUrl(photo.url || photo.relPath);

		return (
			<div style={{
				width: "100%",
				aspectRatio: "1 / 1",
				borderRadius: 8,
				border: "1px solid #f0f0f0",
				overflow: "hidden",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: "#e8e8e8",
			}}>
				<img
					src={imgSrc}
					alt=""
					style={{
						maxWidth: "100%",
						maxHeight: "100%",
						aspectRatio: cc ? `${cc.width} / ${cc.height}` : undefined,
						objectFit: "contain",
					}}
					draggable={false}
				/>
			</div>
		);
	}, [buildPhotoRenderUrl]);

	const fetchAvailableRefs = React.useCallback(async () =>
	{
		if (!flyerId) return;
		setRefsLoading(true);
		try
		{
			const pageSize = 100;
			let skip = 0;
			let total = null;
			let iterations = 0;
			let all = [];

			while (iterations < 50)
			{
				const {data: resp} = await api.get(`/api/flyers/${flyerId}/refs`, {
					params: {
						markAvailable: 1,
						exceptCardId: cardId,
						page: value?.page,
						limit: pageSize,
						skip
					}
				});
				const items = Array.isArray(resp?.items) ? resp.items : [];
				const currentTotal = Number.isFinite(resp?.total) ? resp.total : total;

				total = currentTotal ?? items.length ?? 0;
				all = all.concat(items);

				if (items.length === 0) break;
				if (Number.isFinite(total) && all.length >= total) break;

				skip += pageSize;
				iterations += 1;
			}

			setRefs(all);
		}
		catch (e)
		{
			message.error("Impossibile caricare le referenze");
		}
		finally
		{
			setRefsLoading(false);
		}
	}, [flyerId, cardId, message, value?.page]);

	const validatePhotoUpload = React.useCallback((file) =>
	{
		if (disabled) return Upload.LIST_IGNORE;
		const ext = (file?.name || "").substring((file?.name || "").lastIndexOf(".")).toLowerCase();
		if (!PRODUCT_PHOTO_EXTS.has(ext))
		{
			message.error("Formato non supportato. Usa file WEBP, GIF, PNG, JPG, PSD, AI, PDF o TIFF.");
			return Upload.LIST_IGNORE;
		}
		return true;
	}, [disabled, message]);

	const uploadPhotoFile = React.useCallback(async (file, onProgress) =>
	{
		const formData = new FormData();
		formData.append("photo", file);

		try
		{
			setPhotoProcessing(true);
			const {data: uploadResp} = await api.post(`/api/products/${cardId}/photo/upload`, formData, {
				onUploadProgress: (progressEvent) =>
				{
					const total = progressEvent?.total || 0;
					const loaded = progressEvent?.loaded || 0;
					const percent = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
					onProgress?.(Math.min(90, percent));
				}
			});
			if (uploadResp.ok && uploadResp.relPath)
			{
				onProgress?.(95);
				const {data: appendResp} = await api.post(`/api/products/${cardId}/photo`, {
					mode: "append",
					relPath: uploadResp.relPath
				});
				if (!appendResp?.ok || !appendResp?.card)
					throw new Error(appendResp?.error || "Errore associazione foto");
				onServerCardUpdate?.(appendResp.card);
				onProgress?.(100);
				setPhotoCacheKey(Date.now());
				if (refsOpen)
					await fetchAvailableRefs();
				message.success("Foto caricata e associata");
			}
			else
			{
				throw new Error(uploadResp.error || "Errore upload");
			}
		}
		catch (e)
		{
			console.error(e);
			const msg = e?.response?.data?.error || e?.message || "Impossibile caricare il file";
			message.error(msg);
		}
		finally
		{
			setPhotoProcessing(false);
		}
	}, [cardId, onServerCardUpdate, message, refsOpen, fetchAvailableRefs]);

	const handlePhotoFiles = React.useCallback(async (fileList) =>
	{
		const files = Array.from(fileList || []);
		const validFiles = files.filter((file) => validatePhotoUpload(file) === true);
		if (!validFiles.length)
			return;

		const queuedBatch = photoUploadQueueRef.current
			.catch(() => undefined)
			.then(async () =>
			{
				setPhotoUploadProgress({active: true, percent: 0, current: 0, total: validFiles.length});
				for (let index = 0; index < validFiles.length; index += 1)
				{
					const file = validFiles[index];
					await uploadPhotoFile(file, (filePercent) =>
					{
						const aggregatePercent = Math.round((((index + (filePercent / 100)) / validFiles.length) * 100));
						setPhotoUploadProgress({active: true, percent: aggregatePercent, current: index + 1, total: validFiles.length});
					});
					setPhotoUploadProgress({
						active: true,
						percent: Math.round((((index + 1) / validFiles.length) * 100)),
						current: index + 1,
						total: validFiles.length
					});
				}
				await new Promise((resolve) => setTimeout(resolve, 300));
			})
			.finally(() =>
			{
				setPhotoUploadProgress({active: false, percent: 0, current: 0, total: 0});
			});

		photoUploadQueueRef.current = queuedBatch;
		await queuedBatch;
	}, [validatePhotoUpload, uploadPhotoFile]);

	const handlePhotoInputChange = React.useCallback(async (event) =>
	{
		const files = event.target.files;
		if (files?.length)
			await handlePhotoFiles(files);
		event.target.value = "";
	}, [handlePhotoFiles]);

	const handlePhotoDragOver = React.useCallback((event) =>
	{
		if (disabled || photoProcessing)
			return;
		event.preventDefault();
		setPhotoDragActive(true);
	}, [disabled, photoProcessing]);

	const handlePhotoDragLeave = React.useCallback((event) =>
	{
		if (event.currentTarget.contains(event.relatedTarget))
			return;
		setPhotoDragActive(false);
	}, []);

	const handlePhotoDrop = React.useCallback(async (event) =>
	{
		if (disabled || photoProcessing)
			return;
		event.preventDefault();
		setPhotoDragActive(false);
		const files = event.dataTransfer?.files;
		if (files?.length)
			await handlePhotoFiles(files);
	}, [disabled, photoProcessing, handlePhotoFiles]);

	const handleOpenRefs = React.useCallback(() =>
	{
		setRefsOpen(true);
		fetchAvailableRefs();
	}, [fetchAvailableRefs]);

	const handleSelectRef = React.useCallback(async (ref) =>
	{
		if (!ref?.relPath || !cardId) return;
		try
		{
			setPhotoProcessing(true);
			const {data: resp} = await api.post(`/api/products/${cardId}/photo`, {
				mode: "append",
				relPath: ref.relPath
			});
			if (!resp?.ok || !resp?.card)
				throw new Error(resp?.error || "Immagine non disponibile");
			onServerCardUpdate?.(resp.card);
			setPhotoCacheKey(Date.now());
			await fetchAvailableRefs();
			message.success("Immagine associata");
		}
		catch (e)
		{
			const msg = e?.response?.data?.error || "Immagine non disponibile";
			message.error(msg);
		}
		finally
		{
			setPhotoProcessing(false);
		}
	}, [cardId, onServerCardUpdate, message, fetchAvailableRefs]);

	const handleSetMainPhoto = React.useCallback(async (item) =>
	{
		if (!cardId || !item?.relPath)
			return;
		try
		{
			setPhotoProcessing(true);
			const {data: resp} = await api.patch(`/api/products/${cardId}/photo/main`, {
				relPath: item.relPath
			});
			if (!resp?.ok || !resp?.card)
				throw new Error(resp?.error || "Errore impostazione foto principale");
			onServerCardUpdate?.(resp.card);
			setPhotoCacheKey(Date.now());
			message.success("Immagine principale aggiornata");
		}
		catch (e)
		{
			const msg = e?.response?.data?.error || e?.message || "Errore impostazione foto principale";
			message.error(msg);
		}
		finally
		{
			setPhotoProcessing(false);
		}
	}, [cardId, onServerCardUpdate, message]);

	const handleRemovePhoto = React.useCallback(async (item) =>
	{
		try
		{
			if (!cardId)
				return;
			if (!item?.relPath) {
				message.error("Impossibile rimuovere la foto selezionata");
				return;
			}
			setPhotoProcessing(true);
			const {data: resp} = await api.delete(`/api/products/${cardId}/photo`, {
				params: {mode: "removeOne", relPath: item.relPath}
			});
			if (!resp?.ok || !resp?.card)
				throw new Error(resp?.error || "Errore rimozione foto");
			onServerCardUpdate?.(resp.card);
			setPhotoCacheKey(Date.now());
			if (refsOpen)
				await fetchAvailableRefs();
			message.success("Foto rimossa");
		}
		catch (e)
		{
			const msg = e?.response?.data?.error || e?.message || "Errore rimozione foto";
			message.error(msg);
		}
		finally
		{
			setPhotoProcessing(false);
		}
	}, [cardId, onServerCardUpdate, message, refsOpen, fetchAvailableRefs]);

	// Crea una callback stabile per onChange usando useCallback
	const handleFieldChange = React.useCallback((key, value) =>
	{
		onChange?.(key, value);
	}, [onChange]);

	const handleCustomerCodeChange = React.useCallback((key, value) =>
		handleFieldChange(key, normalizeCustomerCodeInput(value)), [handleFieldChange]);

	// Filtri e callback memoizzati per Select
	const categoryFilterOption = React.useCallback((input, option) =>
			(option?.searchValue ?? "").includes(String(input).toLowerCase()),
		[]
	);

	const classFilterOption = React.useCallback((input, option) =>
			(option?.searchValue ?? "").includes(String(input).toLowerCase()),
		[]
	);

	const fundFilterOption = React.useCallback((input, option) =>
			(option?.searchValue ?? "").includes(String(input).toLowerCase()),
		[]
	);

	const badgeFilterOption = React.useCallback((input, option) =>
	{
		const haystack = (option?.searchValue ?? String(option?.label ?? "")).toLowerCase();
		return haystack.includes(String(input).toLowerCase());
	}, []);

	const filterFilterOption = React.useCallback((input, option) =>
			(option?.searchValue ?? "").includes(String(input).toLowerCase()),
		[]
	);

	const mechanicFilterOption = React.useCallback((input, option) =>
			(option?.searchValue ?? "").includes(String(input).toLowerCase()),
		[]
	);

	const renderField = React.useCallback((field) =>
	{
		const {key, label, type} = field;

		if (key === "categoria")
		{
			return (
				<FloatingField key={key} label={label}>
					<Space size={4} className="field-copy-actions" style={{marginBottom: 4}}>
						<Button
							type="text"
							size="small"
							icon={<CopyOutlined style={{color: fieldIconColor("categoria")}}/>}
							onClick={() => categoryClipboard.copy(data?.categoria ? String(data.categoria) : null)}
							disabled={disabled || !data?.categoria}
						>
							Copia
						</Button>
						<Button
							type="text"
							size="small"
							icon={<SnippetsOutlined style={{color: fieldIconColor("categoria")}}/>}
							onClick={() => handleFieldChange(key, categoryClipboard.value || null)}
							disabled={disabled || !categoryClipboard.hasValue}
						>
							Incolla
						</Button>
					</Space>
					<Select
						showSearch
						allowClear
						placeholder={label}
						options={categoryOptions}
						value={data?.categoria ? String(data.categoria) : undefined}
						filterOption={categoryFilterOption}
						onChange={(optionValue) => handleFieldChange(key, optionValue || null)}
						loading={categoryLoading}
						onSearch={setCategorySearch}
						onInputKeyDown={(e) =>
						{
							if (e.key === 'Enter')
							{
								const trimmed = categorySearch.trim();
								if (trimmed && onCreateCategory && !categoryOptions.some((opt) => (opt?.raw?.name || opt?.value || "").toLowerCase() === trimmed.toLowerCase()))
								{
									e.preventDefault();
									e.stopPropagation();
									handleAddCategory();
								}
							}
						}}
						onOpenChange={(open) =>
						{
							if (!open) setCategorySearch("");
						}}
						notFoundContent={categoryLoading ? "Caricamento..." : "Nessuna categoria"}
						popupRender={(menu) => (
							<>
								{menu}
								{categorySearch &&
									onCreateCategory &&
									!categoryOptions.some(
										(opt) =>
											(opt?.raw?.name || opt?.value || "").toLowerCase() ===
											categorySearch.toLowerCase(),
									) && (
										<>
											<Divider style={{margin: "8px 0"}}/>
											<Space style={{padding: "0 8px 4px"}}>
												<Button
													type="text"
													size="small"
													icon={<PlusOutlined/>}
													onMouseDown={(event) => event.preventDefault()}
													onClick={handleAddCategory}
													disabled={disabled}
												>
													Aggiungi "{categorySearch}"
												</Button>
											</Space>
										</>
									)}
							</>
						)}
						disabled={disabled}
					/>
				</FloatingField>
			);
		}

		if (key === "classe")
		{
			return (
				<FloatingField key={key} label={label}>
					<Space size={4} className="field-copy-actions" style={{marginBottom: 4}}>
						<Button
							type="text"
							size="small"
							icon={<CopyOutlined style={{color: fieldIconColor("classe")}}/>}
							onClick={() => classClipboard.copy(data?.classe ? String(data.classe) : null)}
							disabled={disabled || !data?.classe}
						>
							Copia
						</Button>
						<Button
							type="text"
							size="small"
							icon={<SnippetsOutlined style={{color: fieldIconColor("classe")}}/>}
							onClick={() => handleFieldChange(key, classClipboard.value || null)}
							disabled={disabled || !classClipboard.hasValue}
						>
							Incolla
						</Button>
					</Space>
					<Select
						showSearch
						allowClear
						placeholder={label}
						options={classOptions.map((opt) => ({
							...opt,
							value: String(opt.value),
						}))}
						value={data?.classe ? String(data.classe) : undefined}
						filterOption={classFilterOption}
						onChange={(optionValue) => handleFieldChange(key, optionValue || null)}
						loading={classLoading}
						onSearch={setClassSearch}
						onInputKeyDown={(e) =>
						{
							if (e.key === 'Enter')
							{
								const trimmed = classSearch.trim();
								if (trimmed && onCreateClass && !classOptions.some((opt) => (opt?.raw?.name || opt?.value || "").toLowerCase() === trimmed.toLowerCase()))
								{
									e.preventDefault();
									e.stopPropagation();
									handleAddClass();
								}
							}
						}}
						onOpenChange={(open) =>
						{
							if (!open) setClassSearch("");
						}}
						notFoundContent={classLoading ? "Caricamento..." : "Nessuna classe"}
						popupRender={(menu) =>
						{
							const trimmed = classSearch.trim();
							if (!trimmed || !onCreateClass) return menu;
							const exists = classOptions.some(
								(opt) => (opt?.raw?.name || opt?.value || "").toLowerCase() === trimmed.toLowerCase(),
							);
							if (exists) return menu;
							return (
								<>
									{menu}
									<Divider style={{margin: "8px 0"}}/>
									<Space style={{padding: "0 8px 4px"}}>
										<Button
											type="text"
											size="small"
											icon={<PlusOutlined/>}
											onMouseDown={(event) => event.preventDefault()}
											onClick={handleAddClass}
											disabled={disabled}
										>
											Aggiungi "{trimmed}"
										</Button>
									</Space>
								</>
							);
						}}
						disabled={disabled}
					/>
				</FloatingField>
			);
		}

		if (key === "fondi")
		{
			return (
				<FloatingField key={key} label={label}>
					<Space size={4} className="field-copy-actions" style={{marginBottom: 4}}>
						<Button
							type="text"
							size="small"
							icon={<CopyOutlined style={{color: fieldIconColor("fondi")}}/>}
							onClick={() => fundClipboard.copy(data?.fondi ? String(data.fondi) : null)}
							disabled={disabled || !data?.fondi}
						>
							Copia
						</Button>
						<Button
							type="text"
							size="small"
							icon={<SnippetsOutlined style={{color: fieldIconColor("fondi")}}/>}
							onClick={() => handleFieldChange(key, fundClipboard.value || null)}
							disabled={disabled || !fundClipboard.hasValue}
						>
							Incolla
						</Button>
					</Space>
					<Select
						showSearch
						allowClear
						placeholder={label}
						options={fundOptions.map((opt) => ({
							...opt,
							value: String(opt.value),
						}))}
						value={data?.fondi ? String(data.fondi) : undefined}
						filterOption={fundFilterOption}
						onChange={(optionValue) => handleFieldChange(key, optionValue || null)}
						loading={fundLoading}
						onSearch={setFundSearch}
						onInputKeyDown={(e) =>
						{
							if (e.key === 'Enter')
							{
								const trimmed = fundSearch.trim();
								if (trimmed && onCreateFund && !fundOptions.some((opt) => (opt?.raw?.name || opt?.value || "").toLowerCase() === trimmed.toLowerCase()))
								{
									e.preventDefault();
									e.stopPropagation();
									handleAddFund();
								}
							}
						}}
						onOpenChange={(open) =>
						{
							if (!open) setFundSearch("");
						}}
						notFoundContent={fundLoading ? "Caricamento..." : "Nessun fondo"}
						popupRender={(menu) =>
						{
							const trimmed = fundSearch.trim();
							if (!trimmed || !onCreateFund) return menu;
							const exists = fundOptions.some(
								(opt) => (opt?.raw?.name || opt?.value || "").toLowerCase() === trimmed.toLowerCase(),
							);
							if (exists) return menu;
							return (
								<>
									{menu}
									<Divider style={{margin: "8px 0"}}/>
									<Space style={{padding: "0 8px 4px"}}>
										<Button
											type="text"
											size="small"
											icon={<PlusOutlined/>}
											onMouseDown={(event) => event.preventDefault()}
											onClick={handleAddFund}
											disabled={disabled}
										>
											Aggiungi "{trimmed}"
										</Button>
									</Space>
								</>
							);
						}}
						disabled={disabled}
					/>
				</FloatingField>
			);
		}

		if (key === "bollino")
		{
			const badgeValue = Array.isArray(data?.bollino)
				? data.bollino.map(v => String(v))
				: [];

			return (
				<FloatingField key={key} label={label}>
					<Space size={4} className="field-copy-actions" style={{marginBottom: 4}}>
						<Button
							type="text"
							size="small"
							icon={<CopyOutlined style={{color: fieldIconColor("bollino")}}/>}
							onClick={() => badgeClipboard.copy(badgeValue)}
							disabled={disabled || !badgeValue.length}
						>
							Copia
						</Button>
						<Button
							type="text"
							size="small"
							icon={<SnippetsOutlined style={{color: fieldIconColor("bollino")}}/>}
							onClick={() => handleFieldChange(key, badgeClipboard.value || [])}
							disabled={disabled || !badgeClipboard.hasValue}
						>
							Incolla
						</Button>
					</Space>
					<Select
						mode="multiple"
						showSearch
						allowClear
						placeholder={label}
						options={badgeOptions}
						tagRender={makeTagRender("bollino", badgeClipboard.copy)}
						value={badgeValue}
						filterOption={badgeFilterOption}
						onChange={(values) =>
						{
							// values è già un array di stringhe
							handleFieldChange(key, Array.isArray(values) ? values : []);
						}}
						onSearch={setBadgeSearch}
						onInputKeyDown={(e) =>
						{
							if (e.key === 'Enter')
							{
								const trimmed = badgeSearch.trim();
								if (trimmed && onCreateBadge && !badgeOptions.some((opt) => (opt?.raw?.name || opt?.value || "").toLowerCase() === trimmed.toLowerCase()))
								{
									e.preventDefault();
									e.stopPropagation();
									handleAddBadge();
								}
							}
						}}
						onOpenChange={(open) =>
						{
							if (!open) setBadgeSearch("");
						}}
						loading={badgeLoading}
						notFoundContent={badgeLoading ? "Caricamento..." : "Nessun bollino"}
						popupRender={(menu) =>
						{
							const trimmed = badgeSearch.trim();
							if (!trimmed || !onCreateBadge) return menu;
							const exists = badgeOptions.some(
								(opt) => (opt?.raw?.name || opt?.value || "").toLowerCase() === trimmed.toLowerCase(),
							);
							if (exists) return menu;
							return (
								<>
									{menu}
									<Divider style={{margin: "8px 0"}}/>
									<Space style={{padding: "0 8px 4px"}}>
										<Button
											type="text"
											size="small"
											icon={<PlusOutlined/>}
											onMouseDown={(event) => event.preventDefault()}
											onClick={handleAddBadge}
											disabled={disabled}
										>
											Aggiungi "{trimmed}"
										</Button>
									</Space>
								</>
							);
						}}
						disabled={disabled}
					/>
				</FloatingField>
			);
		}

		if (key === "filtro")
		{
			const filterValue = Array.isArray(data?.filtro)
				? data.filtro.map(v => String(v))
				: data?.filtro
					? [String(data.filtro)]
					: [];

			return (
				<FloatingField key={key} label={label}>
					<Space size={4} className="field-copy-actions" style={{marginBottom: 4}}>
						<Button
							type="text"
							size="small"
							icon={<CopyOutlined style={{color: fieldIconColor("filtro")}}/>}
							onClick={() => filterClipboard.copy(filterValue)}
							disabled={disabled || !filterValue.length}
						>
							Copia
						</Button>
						<Button
							type="text"
							size="small"
							icon={<SnippetsOutlined style={{color: fieldIconColor("filtro")}}/>}
							onClick={() => handleFieldChange(key, filterClipboard.value || [])}
							disabled={disabled || !filterClipboard.hasValue}
						>
							Incolla
						</Button>
					</Space>
					<Select
						mode="multiple"
						showSearch
						allowClear
						placeholder={label}
						options={filterOptions}
						tagRender={makeTagRender("filtro", filterClipboard.copy)}
						value={filterValue}
						filterOption={filterFilterOption}
						onChange={(values) =>
						{
							handleFieldChange(key, Array.isArray(values) ? values : []);
						}}
						onSearch={setFilterSearch}
						onInputKeyDown={(e) =>
						{
							if (e.key === 'Enter')
							{
								const trimmed = filterSearch.trim();
								if (trimmed && onCreateFilter && !filterOptions.some((opt) => (opt?.raw?.name || opt?.value || "").toLowerCase() === trimmed.toLowerCase()))
								{
									e.preventDefault();
									e.stopPropagation();
									handleAddFilter();
								}
							}
						}}
						onOpenChange={(open) =>
						{
							if (!open) setFilterSearch("");
						}}
						loading={filterLoading}
						notFoundContent={filterLoading ? "Caricamento..." : "Nessun filtro"}
						popupRender={(menu) =>
						{
							const trimmed = filterSearch.trim();
							if (!trimmed || !onCreateFilter) return menu;
							const exists = filterOptions.some(
								(opt) => (opt?.raw?.name || opt?.value || "").toLowerCase() === trimmed.toLowerCase(),
							);
							if (exists) return menu;
							return (
								<>
									{menu}
									<Divider style={{margin: "8px 0"}}/>
									<Space style={{padding: "0 8px 4px"}}>
										<Button
											type="text"
											size="small"
											icon={<PlusOutlined/>}
											onMouseDown={(event) => event.preventDefault()}
											onClick={handleAddFilter}
											disabled={disabled}
										>
											Aggiungi "{trimmed}"
										</Button>
									</Space>
								</>
							);
						}}
						disabled={disabled}
					/>
				</FloatingField>
			);
		}

		if (key === "meccanica")
		{
			const mechanicValue = Array.isArray(data?.meccanica)
				? data.meccanica.map(v => String(v))
				: [];

			return (
				<FloatingField key={key} label={label}>
					<Space size={4} className="field-copy-actions" style={{marginBottom: 4}}>
						<Button
							type="text"
							size="small"
							icon={<CopyOutlined style={{color: fieldIconColor("meccanica")}}/>}
							onClick={() => mechanicClipboard.copy(mechanicValue)}
							disabled={disabled || !mechanicValue.length}
						>
							Copia
						</Button>
						<Button
							type="text"
							size="small"
							icon={<SnippetsOutlined style={{color: fieldIconColor("meccanica")}}/>}
							onClick={() => handleFieldChange(key, mechanicClipboard.value || [])}
							disabled={disabled || !mechanicClipboard.hasValue}
						>
							Incolla
						</Button>
					</Space>
					<Select
						mode="multiple"
						showSearch
						allowClear
						placeholder={label}
						options={mechanicOptions}
						tagRender={makeTagRender("meccanica", mechanicClipboard.copy)}
						value={mechanicValue}
						filterOption={mechanicFilterOption}
						onChange={(values) =>
						{
							// values è già un array di stringhe
							handleFieldChange(key, Array.isArray(values) ? values : []);
						}}
						onSearch={setMechanicSearch}
						onInputKeyDown={(e) =>
						{
							if (e.key === 'Enter')
							{
								const trimmed = mechanicSearch.trim();
								if (trimmed && onCreateMechanic && !mechanicOptions.some((opt) => (opt?.raw?.name || opt?.value || "").toLowerCase() === trimmed.toLowerCase()))
								{
									e.preventDefault();
									e.stopPropagation();
									handleAddMechanic();
								}
							}
						}}
						onOpenChange={(open) =>
						{
							if (!open) setMechanicSearch("");
						}}
						loading={mechanicLoading}
						notFoundContent={mechanicLoading ? "Caricamento..." : "Nessuna meccanica"}
						popupRender={(menu) =>
						{
							const trimmed = mechanicSearch.trim();
							if (!trimmed || !onCreateMechanic) return menu;
							const exists = mechanicOptions.some(
								(opt) => (opt?.raw?.name || opt?.value || "").toLowerCase() === trimmed.toLowerCase(),
							);
							if (exists) return menu;
							return (
								<>
									{menu}
									<Divider style={{margin: "8px 0"}}/>
									<Space style={{padding: "0 8px 4px"}}>
										<Button
											type="text"
											size="small"
											icon={<PlusOutlined/>}
											onMouseDown={(event) => event.preventDefault()}
											onClick={handleAddMechanic}
											disabled={disabled}
										>
											Aggiungi "{trimmed}"
										</Button>
									</Space>
								</>
							);
						}}
						disabled={disabled}
					/>
				</FloatingField>
			);
		}

		if (key === "sellout")
		{
			return <MemoizedDateField key={key} fieldKey={key} label={label} value={data?.sellout} onChange={handleFieldChange} disabled={disabled} mode={field.dateMode}/>;
		}

		if (PRICE_FIELD_KEYS.has(key))
		{
			return <MemoizedTextField key={key} fieldKey={key} label={label} value={data?.[key]} onChange={handleFieldChange} disabled={disabled} isPriceField={true}/>;
		}

		// Tassonomie generiche: Select con creazione al volo, stessa interazione di
		// bollino/meccanica (MULTISELECT, valori multipli) o di categoria/classe (SELECT,
		// valore singolo — es. banner/banner_collegato dopo il cutover del Task 15). La
		// cardinalità viene dal FieldRegistry (field.type), non da genericTaxonomy: senza
		// questo ramo i campi cadrebbero nel fallback testo.
		const genericTaxonomy = genericTaxonomies[key];
		if (genericTaxonomy)
		{
			const multiple = type === "MULTISELECT";
			const selected = multiple
				? (Array.isArray(data?.[key]) ? data[key].map(String) : [])
				: (data?.[key] ? String(data[key]) : undefined);
			const searchText = genericSearch[key] ?? "";
			const typed = searchText.trim();
			const canCreate = Boolean(typed)
				&& !genericTaxonomy.options.some((o) => o.label.toLowerCase() === typed.toLowerCase());
			const setSearchText = (text) => setGenericSearch((prev) => ({...prev, [key]: text}));
			const handleAdd = async () =>
			{
				if (!canCreate)
					return;
				const created = await genericTaxonomy.onCreate(typed);
				if (!created)
					return;
				handleFieldChange(key, multiple ? [...(Array.isArray(selected) ? selected : []), created.value] : created.value);
				setSearchText("");
			};
			return (
				<FloatingField key={key} label={label}>
					<Select
						mode={multiple ? "multiple" : undefined}
						showSearch
						allowClear
						placeholder={label}
						options={genericTaxonomy.options}
						value={selected}
						loading={genericTaxonomy.loading}
						optionFilterProp="label"
						disabled={disabled}
						searchValue={searchText}
						onSearch={setSearchText}
						onChange={(values) => handleFieldChange(key, multiple ? values : (values || null))}
						onOpenChange={(open) =>
						{
							if (!open) setSearchText("");
						}}
						onInputKeyDown={(e) =>
						{
							if (e.key !== "Enter" || !canCreate)
								return;
							e.preventDefault();
							e.stopPropagation();
							handleAdd();
						}}
						popupRender={(menu) =>
						{
							if (!canCreate) return menu;
							return (
								<>
									{menu}
									<Divider style={{margin: "8px 0"}}/>
									<Space style={{padding: "0 8px 4px"}}>
										<Button
											type="text"
											size="small"
											icon={<PlusOutlined/>}
											onMouseDown={(event) => event.preventDefault()}
											onClick={handleAdd}
											disabled={disabled}
										>
											Aggiungi "{typed}"
										</Button>
									</Space>
								</>
							);
						}}
					/>
				</FloatingField>
			);
		}

		if (type === "TEXTAREA")
		{
			// Descrizione e descrizione estesa partono più alte di default e crescono senza limite
			// di righe: la cliente non vuole scroll interno, la card si allunga se il testo è lungo.
			// Gli altri TEXTAREA (custom da Settings) restano compatti con un tetto massimo.
			const autoSize = (key === "descrizione" || key === "descrizione_estesa") ? {minRows: 4} : {minRows: 1, maxRows: 3};
			const currentTextValue = data?.[key] || "";
			return (
				<div key={key} className="floating-field product-field-actions-wrap">
					<b><em>{label}</em></b>
					<Space size={4} className="field-copy-actions" style={{marginBottom: 4}}>
						<Button
							type="text"
							size="small"
							icon={<CopyOutlined style={{color: fieldIconColor(key)}}/>}
							onClick={() => genericTextClipboard.copy(key, currentTextValue)}
							disabled={disabled || !currentTextValue}
						>
							Copia
						</Button>
						<Button
							type="text"
							size="small"
							icon={<SnippetsOutlined style={{color: fieldIconColor(key)}}/>}
							onClick={() => handleFieldChange(key, genericTextClipboard.entry?.value ?? "")}
							disabled={disabled || !genericTextClipboard.canPasteInto(key)}
						>
							Incolla
						</Button>
					</Space>
					<MemoizedTextArea fieldKey={key} label="" value={data?.[key]} onChange={handleFieldChange} disabled={disabled} autoSize={autoSize}/>
				</div>
			);
		}

		if (type === "DATE")
		{
			return <MemoizedDateField key={key} fieldKey={key} label={label} value={data?.[key]} onChange={handleFieldChange} disabled={disabled} mode={field.dateMode}/>;
		}

		if (type === "BOOLEAN")
		{
			return <MemoizedSwitchField key={key} fieldKey={key} label={label} value={data?.[key]} onChange={handleFieldChange} disabled={disabled}/>;
		}

		if (type === "NUMBER")
		{
			return <MemoizedNumberField key={key} fieldKey={key} label={label} value={data?.[key]} onChange={handleFieldChange} disabled={disabled}/>;
		}

		// Fallback per i campi testo semplice (TEXT: Note, Focus, Grammatura, Sconto, Codice
		// cliente, o qualunque campo creato su misura in Settings). Stesso clipboard generico
		// usato sopra per i TEXTAREA: condividere lo slot non è un rischio, perché "Incolla" resta
		// comunque abilitato solo sul campo con la stessa chiave di quello copiato.
		{
			const currentTextValue = data?.[key] || "";
			return (
				<div key={key} className="floating-field product-field-actions-wrap">
					<b><em>{label}</em></b>
					<Space size={4} className="field-copy-actions" style={{marginBottom: 4}}>
						<Button
							type="text"
							size="small"
							icon={<CopyOutlined style={{color: fieldIconColor(key)}}/>}
							onClick={() => genericTextClipboard.copy(key, currentTextValue)}
							disabled={disabled || !currentTextValue}
						>
							Copia
						</Button>
						<Button
							type="text"
							size="small"
							icon={<SnippetsOutlined style={{color: fieldIconColor(key)}}/>}
							onClick={() => handleFieldChange(key, genericTextClipboard.entry?.value ?? "")}
							disabled={disabled || !genericTextClipboard.canPasteInto(key)}
						>
							Incolla
						</Button>
					</Space>
					<MemoizedTextField fieldKey={key} label="" value={data?.[key]} onChange={handleFieldChange} disabled={disabled} isPriceField={false}/>
				</div>
			);
		}
	}, [data, categoryOptions, categoryLoading, categorySearch, onCreateCategory, handleAddCategory, disabled, classOptions, classLoading, classSearch, onCreateClass, handleAddClass, fundOptions, fundLoading, fundSearch, onCreateFund, handleAddFund, badgeOptions, badgeLoading, badgeSearch, onCreateBadge, handleAddBadge, filterOptions, filterLoading, filterSearch, onCreateFilter, handleAddFilter, mechanicOptions, mechanicLoading, mechanicSearch, onCreateMechanic, handleAddMechanic, handleFieldChange, categoryFilterOption, classFilterOption, fundFilterOption, badgeFilterOption, filterFilterOption, mechanicFilterOption, mechanicClipboard.value, mechanicClipboard.hasValue, mechanicClipboard.copy, badgeClipboard.value, badgeClipboard.hasValue, badgeClipboard.copy, filterClipboard.value, filterClipboard.hasValue, filterClipboard.copy, categoryClipboard.value, categoryClipboard.hasValue, categoryClipboard.copy, classClipboard.value, classClipboard.hasValue, classClipboard.copy, fundClipboard.value, fundClipboard.hasValue, fundClipboard.copy, genericTextClipboard.entry, genericTextClipboard.copy, genericTextClipboard.canPasteInto, fieldIconColor, genericTaxonomies, genericSearch]);

	// Memoizza le colonne renderizzate per evitare ricreazioni.
	// Deve rimanere prima di qualsiasi return condizionale per rispettare l'ordine dei hook.
	const renderedColumns = React.useMemo(() =>
		columns.map((column, columnIndex) => (
			<div className="fm-col" key={`col-${columnIndex}`}>
				{column.map((field) => renderField(field))}
			</div>
		)), [columns, renderField]);

	let dynamicContent = renderedColumns;
	if (productFieldsLoading)
	{
		dynamicContent = (
			<div className="fm-col">
				<div className="floating-field">
					<b><em>Campi prodotto</em></b>
					<span>Caricamento campi prodotto…</span>
				</div>
			</div>
		);
	}
	else if (!columns.length)
	{
		dynamicContent = (
			<div className="fm-col">
				<div className="floating-field">
					<b><em>Campi prodotto:</em></b>
					<span style={{marginLeft: 10}}>Nessun campo prodotto configurato per la cooperativa selezionata.</span>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="fm-product-layout">
				<div className="fm-product-fixed fm-col">
					{/* Seriale: sola lettura. È l'identità della card (gabbia di pre-elaborazione,
					    import varianti, XML) e il salvataggio card non l'ha mai accettato: la
					    casella modificabile faceva credere di averlo cambiato. Si cambia in
					    pre-elaborazione, prima dell'elaborazione AI. */}
					<Tooltip title={SERIAL_READONLY_HINT} mouseEnterDelay={.3}>
						<div>
							<FloatingField label="Seriale">
								<Input placeholder="Seriale" value={value?.seriale ?? ""} readOnly variant="borderless"/>
							</FloatingField>
						</div>
					</Tooltip>

					{/* Codice cliente: fisso sotto il Seriale per ogni cliente (lo riempie
					    "Carica Codici Clienti" e qui si corregge), quindi escluso dalle colonne
					    dinamiche per non comparire due volte. Va a capo da solo se il testo
					    scritto a mano è lungo. */}
					<MemoizedTextArea fieldKey="codice_cliente" label="Codice cliente" value={data?.codice_cliente} onChange={handleCustomerCodeChange} disabled={disabled} autoSize={{minRows: 1, maxRows: 4}}/>

					{/* Fotoprodotto */}
					<FloatingField label="Fotoprodotto">
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: 12,
								width: "100%",
								padding: 10,
								borderRadius: 12,
								border: photoDragActive ? "1px dashed #1677ff" : "1px dashed transparent",
								background: photoDragActive ? "#f0f7ff" : "transparent",
								transition: "background-color 0.2s ease, border-color 0.2s ease"
							}}
							onDragOver={handlePhotoDragOver}
							onDragLeave={handlePhotoDragLeave}
							onDrop={handlePhotoDrop}
						>
							<div style={{display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8}}>
								{productPhotoItems.map((photo, index) => (
									<div
										key={`${photo.relPath || photo.url || index}-${index}`}
										style={{
											borderRadius: 10,
											border: "1px solid #f0f0f0",
											padding: 8,
											backgroundColor: "#fff"
										}}
									>
										{renderPhotoThumbnail(photo)}
										<div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6}}>
											<span style={{fontSize: 11, color: "#666"}}>{index === 0 ? "Primaria" : `Foto ${index + 1}`}</span>
											{index === 0 && <span style={{fontSize: 10, color: "#1677ff"}}>MAIN</span>}
										</div>
										<div style={{display: "flex", gap: 6, marginTop: 6}}>
											{index > 0 && (
												<Button
													size="small"
													icon={<StarOutlined/>}
													onClick={() => handleSetMainPhoto(photo)}
													disabled={disabled || photoProcessing || !photo.relPath}
													style={{flex: 1}}
													aria-label="Imposta come principale"
													title="Imposta come principale"
												/>
											)}
											<Button
												size="small"
												icon={<EditOutlined/>}
												onClick={() => setEditingPhotoRelPath(photo.relPath)}
												disabled={disabled || photoProcessing || !photo.relPath}
												style={{flex: 1}}
												aria-label="Modifica foto"
											/>
											<Button
												size="small"
												danger
												icon={<DeleteOutlined/>}
												onClick={() => handleRemovePhoto(photo)}
												disabled={disabled || photoProcessing}
												style={{flex: 1}}
												aria-label="Rimuovi foto"
											/>
										</div>
									</div>
								))}

								{!productPhotoItems.length && (
									<div
										style={{
											gridColumn: "1 / -1",
											minHeight: 120,
											borderRadius: 10,
											border: "1px dashed #d9d9d9",
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											gap: 8,
											color: "#999"
										}}
									>
										<FileImageOutlined style={{fontSize: 24}}/>
										<span style={{fontSize: 12}}>Nessuna foto</span>
									</div>
								)}
							</div>

							<input
								ref={photoInputRef}
								type="file"
								accept={PRODUCT_PHOTO_ACCEPT}
								multiple
								onChange={handlePhotoInputChange}
								style={{display: "none"}}
								disabled={disabled || photoProcessing}
							/>

							<div style={{display: "flex", gap: 8}}>
								<Button
									icon={photoUploadProgress.active ? null : <PlusOutlined/>}
									onClick={() => photoInputRef.current?.click()}
									disabled={disabled || photoProcessing}
									style={photoUploadProgress.active ? {
										flex: 1,
										background: `linear-gradient(90deg, #52c41a ${photoUploadProgress.percent}%, #f0f0f0 ${photoUploadProgress.percent}%)`,
										borderColor: "#52c41a",
										color: "rgba(0, 0, 0, 0.88)"
									} : {flex: 1}}
								>
									{photoUploadProgress.active
										? `${photoUploadProgress.percent}%`
										: "Carica"}
								</Button>
								<Button
									icon={<FileImageOutlined/>}
									onClick={handleOpenRefs}
									disabled={disabled || photoProcessing}
									style={{flex: 1}}
								>
									Galleria
								</Button>
							</div>
						</div>
					</FloatingField>
				</div>

				<div className="fm-product-dynamic">
					{dynamicContent}
				</div>
			</div>

			{/* Modal Editor */}
			{
				editingPhoto && (
					<RefEditorModal
						open={!!editingPhoto}
						onClose={() => setEditingPhotoRelPath("")}
						imageSrc={(() =>
						{
							const NON_RENDERABLE = /\.(psd|ai|pdf|tiff?)$/i;
							const orig = editingPhoto?.originalRelPath || "";
							const src = (orig && !NON_RENDERABLE.test(orig)) ? orig : (editingPhoto?.url || "");
							return resolvePhotoUrl(src);
						})()}
						initialComposition={editingPhoto?.composition || null}
						canvasConfig={editingPhoto?.canvasConfig || {width: 1024, height: 1024, fill: "#ffffff", transparency: true, fillPercentage: 100}}
						onSave={(newComposition) => handleSaveComposition(
							newComposition,
							editingPhoto?.canvasConfig || {width: 1024, height: 1024, fill: "#ffffff", transparency: true, fillPercentage: 100},
							editingPhoto?.relPath
						)}
					/>
				)
			}

			<Modal
				open={refsOpen}
				onCancel={() =>
				{
					setRefsOpen(false);
				}}
				title="Seleziona referenza"
				footer={null}
				width={900}
				style={{top: 16, maxWidth: "95vw"}}
				styles={{body: {maxHeight: "calc(100vh - 120px)", overflowY: "auto", paddingRight: 8}}}
			>
				{(() =>
				{
					if (refsLoading) return <div style={{display: 'flex', justifyContent: 'center', padding: 24}}><Spin size="small"/></div>;
					if (!refs.length) return <Alert type="info" showIcon message="Nessuna referenza disponibile"/>;

					const renderRefThumb = (ref) =>
					{
						const isAvailable = ref.available !== false;
						if (!isAvailable) return null;
						return (
							<div
								key={ref.id}
								style={{
									cursor: 'pointer',
									position: 'relative',
								}}
								onClick={() => handleSelectRef(ref)}
							>
								<Image
									src={ref.url.startsWith('/') ? ((import.meta.env.VITE_API_URL || '') + ref.url) : ref.url}
									alt={ref.fileName}
									style={{width: 150, aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 4, border: '1px solid #eee'}}
									preview={false}
								/>
							</div>
						);
					};

					const grouped = groupRefs(refs);

					if (!grouped.hasGroups)
					{
						return (
							<div style={{display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8}}>
								{refs.map(renderRefThumb)}
							</div>
						);
					}

					const cardPage = value?.page;
					const refPageNumbers = Array.from(
						new Set(grouped.groupOrder.map(extractPageNumber).filter(Number.isFinite))
					).sort((a, b) => a - b);
					const cardPages = Array.from(
						new Set((availablePages || []).map(Number).filter(Number.isFinite))
					).sort((a, b) => a - b);
					const directMatchingKeys = cardPage != null
						? grouped.groupOrder.filter((k) => extractPageNumber(k) === cardPage)
						: grouped.groupOrder;
					const directPagesMatch = cardPage == null || directMatchingKeys.length > 0;
					const mappedRefPage = !directPagesMatch && cardPages.length === refPageNumbers.length
						? refPageNumbers[cardPages.indexOf(Number(cardPage))]
						: null;
					const matchingKeys = directPagesMatch
						? directMatchingKeys
						: grouped.groupOrder.filter((k) => extractPageNumber(k) === mappedRefPage);
					const showUngrouped = grouped.ungrouped.length > 0;

					const collapseItems = [
						...matchingKeys.map((groupKey) =>
						{
							const groupItems = grouped.groupedMap.get(groupKey) || [];
							return {
								key: groupKey,
								label: (
									<Space size={8}>
										<FolderOpenOutlined/>
										<Text strong>{groupKey}</Text>
										<Tag color="blue">{groupItems.length}</Tag>
									</Space>
								),
								children: (
									<div style={{display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8}}>
										{groupItems.map(renderRefThumb)}
									</div>
								)
							};
						}),
						...(showUngrouped
							? [{
								key: "__uncategorized__",
								label: (
									<Space size={8}>
										<FolderOpenOutlined/>
										<Text strong>Non classificate</Text>
										<Tag color="default">{grouped.ungrouped.length}</Tag>
									</Space>
								),
								children: (
									<div style={{display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8}}>
										{grouped.ungrouped.map(renderRefThumb)}
									</div>
								)
							}]
							: [])
					];

					if (!collapseItems.length)
					{
						return <Alert type="info" showIcon message="Nessuna referenza disponibile per questa pagina"/>;
					}

					return (
						<Collapse
							bordered={false}
							defaultActiveKey={[
								...matchingKeys,
								...(showUngrouped ? ["__uncategorized__"] : [])
							]}
							items={collapseItems}
						/>
					);
				})()}
			</Modal>
		</>
	);
}
